import type { Template } from "../../../schema/templates";

export const autoPaginationSupportedTemplates = ["onyx", "azurill"] as const satisfies Template[];

export type AutoPaginationSupportedTemplate = (typeof autoPaginationSupportedTemplates)[number];

export type PaginationMode = "auto" | "manual";

export type PaginationModeSource =
  | "created"
  | "missing_preference"
  | "template_switch"
  | "unsupported_template"
  | "user";

export type StoredPaginationPreference = {
  mode: PaginationMode;
  modeSource: PaginationModeSource;
  template: Template | string;
};

export type ResolvedPaginationPreference = {
  mode: PaginationMode;
  modeSource: PaginationModeSource;
  supported: boolean;
};

type PaginationModeDecision = Pick<StoredPaginationPreference, "mode" | "modeSource">;

const supportedTemplateSet = new Set<string>(autoPaginationSupportedTemplates);

export const isAutoPaginationSupportedTemplate = (
  template: Template | string,
): template is AutoPaginationSupportedTemplate => supportedTemplateSet.has(template);

export const getDefaultPaginationModeForCreatedResume = (template: Template | string): PaginationModeDecision => ({
  mode: isAutoPaginationSupportedTemplate(template) ? "auto" : "manual",
  modeSource: "created",
});

export const resolvePaginationModeForTemplateSwitch = (template: Template | string): PaginationModeDecision => {
  if (!isAutoPaginationSupportedTemplate(template)) {
    return { mode: "manual", modeSource: "unsupported_template" };
  }

  return { mode: "auto", modeSource: "template_switch" };
};

export const resolveEffectivePaginationPreference = ({
  preference,
  template,
}: {
  preference: StoredPaginationPreference | null | undefined;
  template: Template | string;
}): ResolvedPaginationPreference => {
  const supported = isAutoPaginationSupportedTemplate(template);

  if (!supported) {
    return { mode: "manual", modeSource: "unsupported_template", supported: false };
  }

  if (!preference) {
    return { mode: "manual", modeSource: "missing_preference", supported: true };
  }

  return { mode: preference.mode, modeSource: preference.modeSource, supported: true };
};
